import React from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Plus } from '@phosphor-icons/react'
import { useAuth } from '../../hooks/useAuth'
import { cn } from '../../lib/utils'
import { logError } from '../../utils/logger'
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar'
import { Button } from '../ui/foundation'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu'
import {
  APP_HEADER_SURFACE_CLASS,
  HEADER_HEIGHT_CLASS,
  HEADER_INNER_CLASS,
} from './headerStyles'
import { navigateWithPageTransition } from '../../utils/pageTransitions'

export interface GlobalTopBarProps {
  className?: string
  centerContent?: React.ReactNode
  userName?: string
  userAvatarUrl?: string | null
  onHomeClick?: () => void
  onCreateBoard?: () => void
  onOrdersClick?: () => void
  onAccountClick?: () => void
  onSupportClick?: () => void
  onSignOutClick?: () => Promise<void> | void
}

export const GlobalTopBar: React.FC<GlobalTopBarProps> = ({
  className,
  centerContent,
  userName,
  userAvatarUrl,
  onHomeClick,
  onCreateBoard,
  onOrdersClick,
  onAccountClick,
  onSupportClick,
  onSignOutClick,
}) => {
  const navigate = useNavigate()
  const { signOut } = useAuth()
  const accountMenuTriggerRef = React.useRef<HTMLButtonElement | null>(null)

  const resolvedName = userName?.trim() || 'User'
  const userInitial = resolvedName.charAt(0).toUpperCase()

  const handleDraftsClick = () => {
    navigateWithPageTransition(navigate, '/drafts')
  }

  const handleCreateBoard = () => {
    if (onCreateBoard) {
      onCreateBoard()
      return
    }

    navigate('/board/new')
  }

  const handleOrdersClick = () => {
    if (onOrdersClick) {
      onOrdersClick()
      return
    }

    navigateWithPageTransition(navigate, '/prints')
  }

  const handleAccountClick = () => {
    if (onAccountClick) {
      onAccountClick()
      return
    }

    navigateWithPageTransition(navigate, '/account')
  }

  const handleSupportClick = () => {
    if (onSupportClick) {
      onSupportClick()
      return
    }

    navigateWithPageTransition(navigate, '/support')
  }

  const handleSignOutClick = async () => {
    try {
      if (onSignOutClick) {
        await onSignOutClick()
        return
      }

      await signOut()
      navigate('/login')
    } catch (error) {
      logError('GlobalTopBar', 'Failed to sign out', error)
    }
  }

  return (
    <header
      className={cn(
        `${HEADER_HEIGHT_CLASS} w-full ${APP_HEADER_SURFACE_CLASS}`,
        className
      )}
    >
      <div className={cn(HEADER_INNER_CLASS, 'gap-3')}>
        <Link
          to="/boards"
          onClick={(event) => {
            if (!onHomeClick) return
            event.preventDefault()
            onHomeClick()
          }}
          aria-label="Back to boards"
          className="inline-flex items-center gap-2 text-ink-700 no-underline transition-colors duration-settle hover:text-primary-500"
        >
          <img
            src="/favicon-brown.svg"
            width={20}
            height={20}
            alt=""
            aria-hidden="true"
          />
          <span
            className="hidden font-sans text-fs-300 font-medium sm:inline"
            style={{
              letterSpacing: '0.01em',
              lineHeight: 1,
            }}
          >
            Printboard
          </span>
        </Link>

        <div className="flex-1 min-w-0 px-2 text-center">{centerContent}</div>

        <div className="flex items-center gap-1 sm:gap-2">
          <Button
            variant="ghost"
            size="sm"
            icon
            onClick={handleCreateBoard}
            aria-label="Create new board"
            title="Create new board"
          >
            <Plus className="h-4 w-4" />
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                ref={accountMenuTriggerRef}
                className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-neutral-200 bg-background-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400/35"
                aria-label="Account menu"
                title="Account"
              >
                <Avatar className="h-8 w-8">
                  {userAvatarUrl ? (
                    <AvatarImage src={userAvatarUrl} alt={resolvedName} />
                  ) : null}
                  <AvatarFallback className="bg-primary-400 text-background-50 text-xs font-medium">
                    {userInitial}
                  </AvatarFallback>
                </Avatar>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="w-44"
              onCloseAutoFocus={(event) => {
                event.preventDefault()
                accountMenuTriggerRef.current?.blur()
              }}
            >
              <DropdownMenuItem onClick={handleDraftsClick}>
                Drafts
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleOrdersClick}>
                Prints
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleAccountClick}>
                Account
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleSupportClick}>
                Support
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                variant="destructive"
                onClick={() => void handleSignOutClick()}
              >
                Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  )
}

export default GlobalTopBar
